/**
 * selectors.js
 * Selectors memoizados usando createSelector do Redux Toolkit
 * Segue o padrão do Traccar (useFeatures.js)
 *
 * Beneficios:
 * - Evita re-renders quando dados derivados não mudaram
 * - Memoiza computações caras (Object.values, counts, filtros)
 * - Centraliza lógica de seleção de dados
 */

import { createSelector } from '@reduxjs/toolkit';
import { useSelector, shallowEqual } from 'react-redux';
import { getEffectiveStatus, isDeviceMoving } from '../common/util/deviceColors';

// ============================================================
// INPUT SELECTORS (não memoizados - retornam slice do state)
// ============================================================

export const selectDevicesItems = (state) => state.devices.items;
export const selectSelectedDeviceId = (state) => state.devices.selectedId;
export const selectSessionPositions = (state) => state.session.positions;
export const selectSessionHistory = (state) => state.session.history;
export const selectEventsItems = (state) => state.events.items;
export const selectEventsById = (state) => state.events.byId;
export const selectGroupsItems = (state) => state.groups.items;
export const selectServer = (state) => state.session.server;
export const selectUser = (state) => state.session.user;

// ============================================================
// DERIVED SELECTORS (memoizados com createSelector)
// ============================================================

/**
 * Array de devices - memoizado
 * Só recalcula quando devices.items muda
 */
export const selectDevicesArray = createSelector(
  [selectDevicesItems],
  (devices) => Object.values(devices),
);

/**
 * Array de device IDs - memoizado
 * Útil para iteração sem criar novos arrays
 */
export const selectDeviceIds = createSelector(
  [selectDevicesItems],
  (devices) => Object.keys(devices),
);

/**
 * Total de devices - memoizado
 */
export const selectDevicesCount = createSelector(
  [selectDeviceIds],
  (ids) => ids.length,
);

/**
 * Device selecionado - memoizado
 */
export const selectSelectedDevice = createSelector(
  [selectDevicesItems, selectSelectedDeviceId],
  (devices, selectedId) => (selectedId ? devices[selectedId] : null),
);

/**
 * Posição do device selecionado - memoizado
 */
export const selectSelectedPosition = createSelector(
  [selectSessionPositions, selectSelectedDeviceId],
  (positions, selectedId) => (selectedId ? positions[selectedId] : null),
);

/**
 * Device selecionado com sua posição - memoizado
 * Combina device e position em um objeto único
 */
export const selectSelectedDeviceWithPosition = createSelector(
  [selectSelectedDevice, selectSelectedPosition],
  (device, position) => {
    if (!device) return null;
    return { device, position };
  },
);

/**
 * Contadores de status - memoizado
 * Calcula moving, stopped, offline, unknown e total
 * Só recalcula quando devices ou positions mudam
 */
export const selectDeviceStatusCounts = createSelector(
  [selectDevicesItems, selectSessionPositions],
  (devices, positions) => {
    const counts = {
      moving: 0,
      stopped: 0,
      online: 0,
      offline: 0,
      unknown: 0,
      total: 0,
    };

    const deviceValues = Object.values(devices);
    counts.total = deviceValues.length;

    deviceValues.forEach((device) => {
      const position = positions[device.id];
      const moving = isDeviceMoving(position);
      const effectiveStatus = getEffectiveStatus(
        device.status,
        moving,
        !!position,
      );
      counts[effectiveStatus] = (counts[effectiveStatus] || 0) + 1;
    });

    return counts;
  },
);

/**
 * Array de positions - memoizado
 * Útil para MapPositions e outros componentes de mapa
 */
export const selectPositionsArray = createSelector(
  [selectSessionPositions],
  (positions) => Object.values(positions),
);

/**
 * Array de events - já é array, mas selector para consistência
 */
export const selectEventsArray = createSelector(
  [selectEventsItems],
  (events) => events,
);

/**
 * Devices por grupo - memoizado
 * Retorna Map<groupId, Device[]>
 */
export const selectDevicesByGroup = createSelector(
  [selectDevicesArray],
  (devices) => {
    const byGroup = new Map();
    devices.forEach((device) => {
      const groupId = device.groupId || 0;
      if (!byGroup.has(groupId)) {
        byGroup.set(groupId, []);
      }
      byGroup.get(groupId).push(device);
    });
    return byGroup;
  },
);

/**
 * Devices online (com posição) - memoizado
 */
export const selectOnlineDevices = createSelector(
  [selectDevicesArray, selectSessionPositions],
  (devices, positions) => devices.filter((device) => positions[device.id]),
);

/**
 * Devices offline (sem posição) - memoizado
 */
export const selectOfflineDevices = createSelector(
  [selectDevicesArray, selectSessionPositions],
  (devices, positions) => devices.filter((device) => !positions[device.id]),
);

// ============================================================
// FACTORY SELECTORS (para parâmetros dinâmicos)
// ============================================================

/**
 * Cria selector para device específico por ID
 * Uso: const selectDevice = makeSelectDeviceById(deviceId)
 */
export const makeSelectDeviceById = (deviceId) => createSelector(
  [selectDevicesItems],
  (devices) => devices[deviceId] || null,
);

/**
 * Cria selector para position específica por deviceId
 */
export const makeSelectPositionByDeviceId = (deviceId) => createSelector(
  [selectSessionPositions],
  (positions) => positions[deviceId] || null,
);

/**
 * Cria selector para event específico por ID - O(1) lookup
 */
export const makeSelectEventById = (eventId) => createSelector(
  [selectEventsById],
  (eventsById) => eventsById[eventId] || null,
);

// ============================================================
// HOOKS (para uso conveniente em componentes)
// ============================================================

/**
 * Hook para devices array memoizado
 */
export const useDevices = () => useSelector(selectDevicesArray, shallowEqual);

/**
 * Hook para device IDs
 */
export const useDeviceIds = () => useSelector(selectDeviceIds, shallowEqual);

/**
 * Hook para total de devices
 */
export const useDevicesCount = () => useSelector(selectDevicesCount);

/**
 * Hook para device selecionado
 */
export const useSelectedDevice = () => useSelector(selectSelectedDevice);

/**
 * Hook para posição do device selecionado
 */
export const useSelectedPosition = () => useSelector(selectSelectedPosition);

/**
 * Hook para device + position combinados
 */
export const useSelectedDeviceWithPosition = () => useSelector(selectSelectedDeviceWithPosition);

/**
 * Hook para contadores de status
 * Retorna { moving, stopped, online, offline, unknown, total }
 */
export const useDeviceStatusCounts = () => useSelector(selectDeviceStatusCounts, shallowEqual);

/**
 * Hook para positions array
 */
export const usePositions = () => useSelector(selectPositionsArray, shallowEqual);

/**
 * Hook para events
 */
export const useEvents = () => useSelector(selectEventsArray, shallowEqual);

/**
 * Hook para devices por grupo
 */
export const useDevicesByGroup = () => useSelector(selectDevicesByGroup);
